import { useState } from 'react';
import { api } from '@/lib/api';
import { formatCOP, formatDate } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { RefreshCw, RotateCcw } from 'lucide-react';
import { Pill, Empty, useFetch, runAction } from './shared';

const FILTERS = [
  { key: 'draft', label: 'Pendientes' },
  { key: 'blocked_parent_rejected', label: 'Madre rechazada' },
  { key: 'blocked_invalid', label: 'Inconsistentes' },
  { key: 'failed_max_retries', label: 'Reintentos agotados' },
  { key: 'rejected_dian', label: 'Rechazadas DIAN' },
];
const RETRIABLE = ['failed_max_retries', 'rejected_dian', 'blocked_invalid'];

function CreditNoteRow({ cn, onRetry }) {
  return (
    <tr className="border-t border-slate-100 text-sm" data-testid={`dian-cn-row-${cn.credit_note_id}`}>
      <td className="py-2 pr-3 font-mono text-xs">{cn.credit_note_id}</td>
      <td className="py-2 pr-3 font-mono text-xs">{cn.parent_invoice_number || cn.parent_invoice_id}</td>
      <td className="py-2 pr-3">{cn.correction_concept_code || '—'}</td>
      <td className="py-2 pr-3 font-mono">{formatCOP(cn.total || 0)}</td>
      <td className="py-2 pr-3"><Pill value={cn.status} /></td>
      <td className="py-2 pr-3 font-mono text-xs">{cn.attempts || 0}</td>
      <td className="py-2 pr-3 text-xs text-slate-600 max-w-xs truncate"
        title={cn.blocked_reason || cn.last_error || cn.rejected_reason || ''}>
        {cn.blocked_reason || cn.last_error || cn.rejected_reason || '—'}
      </td>
      <td className="py-2 pr-3 text-xs">{formatDate(cn.created_at)}</td>
      <td className="py-2">
        {RETRIABLE.includes(cn.status) && cn.credit_note_kind !== 'sandbox_test' && (
          <Button size="sm" variant="outline" className="rounded-none h-7"
            data-testid={`dian-cn-retry-${cn.credit_note_id}`} onClick={() => onRetry(cn.credit_note_id)}>
            <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reintentar
          </Button>
        )}
      </td>
    </tr>
  );
}

export function DianCreditNotesPanel({ onChanged }) {
  const [status, setStatus] = useState('draft');
  const { data, loading, load } = useFetch(`/admin/dian/credit-notes?status=${status}&limit=100`);
  const retry = (id) => runAction(() => api.post(`/admin/dian/credit-notes/${id}/retry`),
    'Nota crédito devuelta a pendiente', () => { load(); onChanged && onChanged(); });
  const items = data?.items || [];
  return (
    <div className="space-y-3" data-testid="dian-cn-panel">
      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <Button key={f.key} size="sm" variant={status === f.key ? 'default' : 'outline'}
            className="rounded-none" data-testid={`dian-cn-filter-${f.key}`} onClick={() => setStatus(f.key)}>
            {f.label}
          </Button>
        ))}
        <div className="flex-1" />
        <Button size="sm" variant="ghost" className="rounded-none" data-testid="dian-cn-reload" onClick={load}>
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>
      {items.length === 0 ? <Empty testId="dian-cn-empty">No hay notas crédito en este estado.</Empty> : (
        <div className="overflow-x-auto border border-slate-200 bg-white p-3">
          <table className="w-full text-left" data-testid="dian-cn-table">
            <thead className="text-[10px] uppercase tracking-wider text-slate-500">
              <tr><th>ID</th><th>Factura madre</th><th>Concepto</th><th>Total</th><th>Estado</th><th>Intentos</th><th>Motivo</th><th>Creada</th><th /></tr>
            </thead>
            <tbody>{items.map((cn) => <CreditNoteRow key={cn.credit_note_id} cn={cn} onRetry={retry} />)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
